import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

const WeddingFooter: React.FC = () => {
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Create audio element
    audioRef.current = new Audio();
    audioRef.current.loop = true;
    audioRef.current.volume = 0.3; // Set volume to 30%
    
    // Try to load a background music file
    // You can replace this with your actual music file
    audioRef.current.src = '/background-music.mp3'; // Add your music file to public folder
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const toggleMusic = () => {
    if (!audioRef.current) return;

    if (isMusicPlaying) {
      audioRef.current.pause();
      setIsMusicPlaying(false);
    } else {
      audioRef.current.play().catch((error) => {
        console.log('Could not play music:', error);
        // If music file doesn't exist, show a message
        alert('Background music file not found. Please add a music file named "background-music.mp3" to the public folder.');
      });
      setIsMusicPlaying(true);
    }
  };

  return (
    <footer className="relative overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-rose-900 via-purple-900 to-indigo-900"></div>
      
      {/* Floating Hearts Animation */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-rose-300/20 text-2xl"
            initial={{ 
              x: Math.random() * window.innerWidth,
              y: window.innerHeight + 50,
              rotate: 0
            }}
            animate={{
              y: -100,
              rotate: 360,
            }}
            transition={{
              duration: 8 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "linear"
            }}
          >
            💕
          </motion.div>
        ))}
      </div>

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-8 items-center">
            
            {/* Couple Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center lg:text-left"
            >
              {/* <motion.h3 
                className="text-3xl lg:text-4xl font-great-vibes text-white mb-2"
                whileHover={{ scale: 1.05 }}
              >
                Noor & Priya
              </motion.h3> */}
              <p className="text-rose-200 text-lg mb-3 font-light">
                January 4, 2026
              </p>
              <p className="text-rose-100 text-sm leading-relaxed ">
                "Two souls, one heart, forever together."
              </p>
            </motion.div>

            {/* Love Quote */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              {/* <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                <div className="text-4xl mb-3">💕</div>
                <h4 className="text-white font-semibold mb-3">Our Special Day</h4>
                <p className="text-rose-200 text-sm leading-relaxed italic">
                  "Today marks the beginning of our forever. Every moment with you is a gift I treasure."
                </p>
                <div className="mt-4 text-rose-100 text-xs">
                  January 4, 2026
                </div>
              </div> */}
            </motion.div>

            {/* Social */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-center lg:text-right"
            >
              {/* Instagram Link */}
              <div className="flex justify-center lg:justify-end mb-3">
                <motion.a
                  href="#"
                  whileHover={{ scale: 1.2, rotate: 10 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-full flex items-center justify-center transition-all duration-300 hover:from-purple-600 hover:via-pink-600 hover:to-orange-500"
                  title="Instagram"
                >
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </motion.a>
              </div>

              <p className="text-rose-200 text-xs">
                #NoorAndPriya2026
              </p>
            </motion.div>
          </div>
        </div>

        {/* Bottom Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          viewport={{ once: true }}
          className="relative border-t border-white/20"
        >
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-rose-200 text-sm">
                © 2025 Noor & Priya Wedding Website. Made with 💕 by Noor for Priya.
              </p>
              <div className="flex items-center space-x-2 text-rose-200 text-sm">
                <span>💕</span>
                <span>Forever & Always</span>
                <span>💕</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default WeddingFooter;
